import type { EventMapFromUnion } from '@pkg/server/events'
import { isSSR } from '@pkg/shared/env'
import { logger } from '@pkg/shared/logger'
import { tryParseJson } from '@pkg/shared/utils/api.utils'
import type { MqttClient } from 'mqtt'
import { createNanoEvents, type Emitter, type EventsMap } from 'nanoevents'
import type { z } from 'zod'

export interface IoTMqttListenerOptions<TSchema extends z.ZodTypeAny> {
  /** Zod schema validating incoming event payloads. */
  schema: TSchema
  /** URL of the token mint endpoint. Defaults to '/api/events/token'. */
  tokenUrl?: string
  /** Start the listener immediately on creation. Defaults to true. */
  autostart?: boolean
  /** Called on each schema-validated event. */
  onEvent?: (event: z.infer<TSchema>) => void
  /** Called when the connection re-establishes after a drop. */
  onReconnect?: () => void
}

export interface IoTMqttListener<TEventMap extends EventsMap> {
  events: Emitter<TEventMap>
  start: () => void
  stop: () => void
  isRunning: () => boolean
}

interface TokenMintResponse {
  token: string
  expiresAt: string
  iotEndpoint: string
  authorizerName: string
  topics: string[]
}

const fetchToken = async (url: string): Promise<TokenMintResponse> => {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error(`Token mint failed: ${response.status}`)
  }
  return (await response.json()) as TokenMintResponse
}

export function createIoTMqttListener<TSchema extends z.ZodTypeAny>(
  options: IoTMqttListenerOptions<TSchema>,
): IoTMqttListener<EventMapFromUnion<z.infer<TSchema>>> {
  type TEventUnion = z.infer<TSchema>
  type TEventMap = EventMapFromUnion<TEventUnion>

  const tokenUrl = options.tokenUrl ?? '/api/events/token'
  const events = createNanoEvents<TEventMap>()
  let client: MqttClient | undefined
  let running = false
  let wasConnected = false
  let refreshTimer: ReturnType<typeof setTimeout> | undefined

  const buildConnectUrl = (mint: TokenMintResponse) => {
    const hostname = mint.iotEndpoint.replace(/^https?:\/\//, '').replace(/\/$/, '')
    const params = new URLSearchParams({
      'x-amz-customauthorizer-name': mint.authorizerName,
    })
    return `wss://${hostname}/mqtt?${params.toString()}`
  }

  const scheduleRefresh = (expiresAtIso: string) => {
    if (refreshTimer) clearTimeout(refreshTimer)
    const expiresAtMs = Date.parse(expiresAtIso)
    const nowMs = Date.now()
    // Refresh 5 minutes before expiry, but never sooner than 60 s from now.
    const refreshAfterMs = Math.max(60_000, expiresAtMs - nowMs - 5 * 60_000)
    refreshTimer = setTimeout(() => {
      logger.info({ refreshAfterMs }, 'IoT MQTT: token refresh due, reconnecting')
      void reconnect()
    }, refreshAfterMs)
  }

  const reconnect = async () => {
    if (client) {
      client.end(true)
      client = undefined
    }
    await connectOnce()
  }

  const connectOnce = async () => {
    if (isSSR) return
    let mint: TokenMintResponse
    try {
      mint = await fetchToken(tokenUrl)
    } catch (err) {
      logger.error({ err }, 'IoT MQTT: failed to mint token')
      return
    }

    const url = buildConnectUrl(mint)
    // mqtt pulls in `ws` for its Node transport; a static import drags both into
    // the SSR/Lambda bundle (where Nitro fails to trace `ws`) even though this
    // listener only ever runs in the browser. Import it lazily past the isSSR
    // guard so the server never loads the module.
    const { default: mqtt } = await import('mqtt')
    client = mqtt.connect(url, {
      protocol: 'wss',
      username: 'icare',
      password: mint.token,
      reconnectPeriod: 5_000,
      keepalive: 60,
      clean: true,
      protocolVersion: 5,
    })

    client.on('connect', () => {
      logger.info({ topics: mint.topics }, 'IoT MQTT: connected, subscribing')
      const currentClient = client
      if (currentClient) {
        for (const topic of mint.topics) {
          currentClient.subscribe(topic, { qos: 0 }, err => {
            if (err) logger.error({ err, topic }, 'IoT MQTT: subscribe failed')
          })
        }
      }
      if (wasConnected) {
        options.onReconnect?.()
      }
      wasConnected = true
    })

    client.on('reconnect', () => {
      logger.info('IoT MQTT: reconnecting')
    })

    client.on('error', err => {
      logger.error({ err }, 'IoT MQTT: client error')
    })

    client.on('close', () => {
      logger.warn('IoT MQTT: connection closed')
    })

    client.on('message', (_topic, payloadBuf) => {
      const text = payloadBuf.toString('utf-8')
      const raw = tryParseJson(text, text)
      const result = options.schema.safeParse(raw)
      if (result.success) {
        options.onEvent?.(result.data)
        // @ts-expect-error nanoevents key check is structural at type level; runtime strings are fine.
        events.emit((result.data as { type: string }).type as keyof TEventMap, result.data)
      } else {
        logger.warn({ issues: result.error.issues, raw }, 'IoT MQTT: invalid event payload')
      }
    })

    scheduleRefresh(mint.expiresAt)
  }

  const start = () => {
    if (isSSR || running) return
    running = true
    void connectOnce()
  }

  const stop = () => {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = undefined
    }
    if (client) {
      client.end(true)
      client = undefined
    }
    running = false
    wasConnected = false
  }

  if (options.autostart !== false) {
    start()
  }

  return {
    events,
    start,
    stop,
    isRunning: () => running,
  }
}
